import { Box, CircularProgress } from '@mui/material';
import axios, { HttpStatusCode } from 'axios';
import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import * as Sentry from '@sentry/react';
import { useRedirectIfAuth } from '../hooks/useRedirectIfAuth';
import { PageWrapper } from '../common/components/PageWrapper';
import { decodeState } from '../common/utils/auth.utils';
import useAuth from '../hooks/useAuth/useAuth';
import { RoutePaths } from '../routes/AppRoutes';
import { useOIDC } from './hooks/useOIDC';

const ENDPOINT_BY_DOMAIN: Record<string, string> = {
  'carrefour.com': `${import.meta.env.REACT_APP_SERVER_BASE_URL}/auth/login/carrefour`,
};

export default function OIDCCallbackPage(): React.JSX.Element {
  const RedirectIfAuth = useRedirectIfAuth();
  const { hash } = useLocation();
  const { setLoginToken } = useAuth();
  const [searchParams] = useSearchParams();
  const { startOIDC } = useOIDC();
  const ongoing = useRef(false);
  const navigate = useNavigate();

  const hashParams = useMemo(() => {
    const stringParams = hash.substring(1).split('&');

    return stringParams.reduce(
      (acc, stringParam) => {
        const [key, value] = stringParam.split('=');

        acc.set(key, value);

        return acc;
      },
      new Map(),
    );
  }, [hash]);

  const authState = searchParams.get('state') || hashParams.get('state');
  const authCode = searchParams.get('code') || hashParams.get('code');

  useEffect(() => {
    if (ongoing.current) {
      return;
    }

    ongoing.current = true;

    try {
      if (!authState || !authCode) {
        throw new Error('Auth failed, no code received');
      }

      const state = decodeState(authState);

      if (!state) {
        throw new Error(`Auth failed, bad state: ${JSON.stringify(authState)}`);
      }

      const nonce = localStorage.getItem('ssoAuthNonce');

      axios.post(
        ENDPOINT_BY_DOMAIN[state.domain]!,
        {
          code: authCode,
          nonce,
          email: state.email,
        },
      ).then((response) => {
        setLoginToken(response.data.tokens);
      }).catch((error) => {
        const alreadyAttemptedForceLogin = state.isForcingPrompt;

        if (error.status === HttpStatusCode.BadRequest && !alreadyAttemptedForceLogin) {
          startOIDC(state.email, true);
        } else {
          Sentry.captureException(error);

          navigate(RoutePaths.LOGIN);
        }
      });
    } catch (error) {
      Sentry.captureException(error);

      navigate(RoutePaths.LOGIN);
    }
  }, []);

  return (
    <RedirectIfAuth>
      <PageWrapper>
        <Box sx={{ display: 'flex', width: '100%', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={120} />
        </Box>
      </PageWrapper>
    </RedirectIfAuth>
  );
}
